import { useQuery } from '@tanstack/react-query';

import { CountryTariffs, ProductTariffs } from 'src/model/tariffs';
import PublicService from 'src/services/api/public';
export const QUERY_KEY_PUBLIC_COUNTRY_TARIFFS = 'QUERY_KEY_PUBLIC_COUNTRY_TARIFFS';
const QUERY_KEY_PUBLIC_PRODUCT_HS_CODE_TARIFFS = 'QUERY_KEY_PUBLIC_PRODUCT_HS_CODE_TARIFFS';
export const QUERY_KEY_PUBLIC_PRODUCT_NATIONAL_CODE_TARIFFS = 'QUERY_KEY_PUBLIC_PRODUCT_NATIONAL_CODE_TARIFFS';
const QUERY_KEY_PUBLIC_GET_NTM_MEASURES = 'QUERY_KEY_PUBLIC_GET_NTM_MEASURES';
const QUERY_KEY_PUBLIC_GET_CUSTOM_DUTIES = 'QUERY_KEY_PUBLIC_GET_CUSTOM_DUTIES';
const QUERY_KEY_PUBLIC_GET_TRADE_REMEDY = 'QUERY_KEY_PUBLIC_GET_TRADE_REMEDY';
const QUERY_KEY_PUBLIC_REGIME_COUNTRY_TARIFFS = 'QUERY_KEY_PUBLIC_REGIME_COUNTRY_TARIFFS';
const QUERY_KEY_PUBLIC_FTA_TARIFFS = 'QUERY_KEY_PUBLIC_FTA_TARIFFS';
const QUERY_KEY_PUBLIC_ROO_FTA_TARIFFS = 'QUERY_KEY_PUBLIC_ROO_FTA_TARIFFS';

export const useQueryPublicCountriesTariffs = () => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_COUNTRY_TARIFFS],
    queryFn: () => PublicService.getCountryTariffs(),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new CountryTariffs(),
    placeholderData: (previousData) => previousData,
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicHsCodeTariffs = ({
  countryCode,
  level,
  keyword,
}: {
  countryCode: string;
  level: number;
  keyword: string;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_PRODUCT_HS_CODE_TARIFFS, countryCode, keyword],
    queryFn: () =>
      PublicService.searchHsCodeTariffs({
        countryCode,
        level,
        keyword,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new ProductTariffs(),
    enabled: Boolean(keyword),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicNationalCodeTariffs = ({
  countryCode,
  level,
  hsCode,
}: {
  countryCode: string;
  level: number;
  hsCode: string;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_PRODUCT_NATIONAL_CODE_TARIFFS, countryCode, hsCode],
    queryFn: () =>
      PublicService.searchNationalCodeTariffs({
        countryCode,
        level,
        hsCode,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    initialData: new ProductTariffs(),
    enabled: Boolean(hsCode),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicGetNtmMeasures = ({
  reporter,
  partner,
  product,
}: {
  reporter: string;
  partner: string;
  product: string;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_GET_NTM_MEASURES, reporter, partner, product],
    queryFn: () =>
      PublicService.getNtmMeasures({
        reporter,
        partner,
        product,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(reporter) && Boolean(partner) && Boolean(product),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicGetCustomDuties = ({
  reporter,
  partner,
  product,
}: {
  reporter: string;
  partner: string;
  product: string;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_GET_CUSTOM_DUTIES, reporter, partner, product],
    queryFn: () =>
      PublicService.getCustomDuties({
        reporter,
        partner,
        product,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(reporter) && Boolean(partner) && Boolean(product),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicGetTradeRemedy = ({
  reporter,
  partner,
  product,
}: {
  reporter: string;
  partner: string;
  product: string;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_GET_TRADE_REMEDY, reporter, partner, product],
    queryFn: () =>
      PublicService.getTradeRemedy({
        reporter,
        partner,
        product,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(reporter) && Boolean(partner) && Boolean(product),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicGetRegimeCountryTariffs = ({ reporter, agId }: { reporter: string; agId: number }) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_REGIME_COUNTRY_TARIFFS, reporter, agId],
    queryFn: () =>
      PublicService.getRegimeCountryTariffs({
        reporter,
        agId,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(reporter) && Boolean(agId),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicGetFtaTariffs = ({
  reporter,
  partner,
  product,
  ftaId,
}: {
  reporter: string;
  partner: string;
  product: string;
  ftaId: number;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_FTA_TARIFFS, reporter, partner, product, ftaId],
    queryFn: () =>
      PublicService.getFtaTariffs({
        reporter,
        partner,
        product,
        ftaId,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(reporter) && Boolean(partner) && Boolean(product) && Boolean(ftaId),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};

export const useQueryPublicGetRooFtaTariffs = ({
  reporter,
  partner,
  ftaId,
}: {
  reporter: string;
  partner: string;
  ftaId: number;
}) => {
  const { data, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEY_PUBLIC_ROO_FTA_TARIFFS, reporter, partner, ftaId],
    queryFn: () =>
      PublicService.getRooFtaTariffs({
        reporter,
        partner,
        ftaId,
      }),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(reporter) && Boolean(partner) && Boolean(ftaId),
  });
  return {
    data,
    isFetching,
    refetch,
  };
};
